import { RefreshCw } from "lucide-react";
import { useState } from "react";
import { DateRange } from "react-day-picker";
import { QuickStatsCards } from "./QuickStatsCards";
import { UsageAnalytics } from "./UsageAnalytics";
import { UsersAnalytics } from "./UsersAnalytics";
import { LoadingSpinner } from "@/components/LoadingSpinner/LoadingSpinner";
import { PageWrapper } from "@/components/PageWrapper";
import { Button } from "@/components/ui/button";
import { CalendarDatePicker } from "@/components/ui/calendar-date-picker";
import { useAppSelector } from "@/hooks/types";
import { useDashboardData } from "@/hooks/useDashboardData";
import { CreditsAnalytics } from "@/pages/Portal/Dashboard/CreditsAnalytics";
import { UserJobsAnalytics } from "@/pages/Portal/Dashboard/UserJobsAnalytics";
import {
    getIs1TemplateTenant,
    getIsUserJobsEnabled,
} from "@/store/tenant/selectors";

export const PortalDashboard = () => {
    const [dateRange, setDateRange] = useState<DateRange>({
        from: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
        to: new Date(),
    });
    const is1TemplateTenant = useAppSelector(getIs1TemplateTenant);
    const isUserJobsEnabled = useAppSelector(getIsUserJobsEnabled);

    const { quickStats, transactionsAnalytics, isLoading, isError, refetch } =
        useDashboardData(dateRange);

    const handleDateSelect = (range: { from: Date; to: Date }) => {
        setDateRange({ from: range.from, to: range.to });
    };

    const handleRefresh = () => {
        refetch();
    };

    if (isLoading) {
        return (
            <PageWrapper title="Analytics Dashboard">
                <div className="flex h-64 items-center justify-center">
                    <LoadingSpinner />
                </div>
            </PageWrapper>
        );
    }

    if (isError) {
        return (
            <PageWrapper title="Analytics Dashboard">
                <div className="flex h-64 items-center justify-center ">
                    <div className="rounded-md bg-admin-secondary p-5 text-center">
                        <h4 className="mb-4 font-medium text-admin-foreground">
                            Failed to load dashboard data
                        </h4>
                        <Button
                            onClick={refetch}
                            variant="destructive"
                        >
                            Retry
                        </Button>
                    </div>
                </div>
            </PageWrapper>
        );
    }

    return (
        <PageWrapper
            title="Analytics Dashboard"
            primaryAction={
                <div className="flex items-center gap-4">
                    <Button
                        onClick={handleRefresh}
                        variant="secondary"
                        size="sm"
                        disabled={isLoading}
                    >
                        <RefreshCw
                            className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`}
                        />
                    </Button>
                    <CalendarDatePicker
                        date={dateRange}
                        onDateSelect={handleDateSelect}
                        numberOfMonths={2}
                        closeOnSelect={false}
                    />
                </div>
            }
        >
            <div className="flex w-full flex-1 flex-col gap-6 p-6">
                {/* Quick Stats Cards */}
                <QuickStatsCards data={quickStats} />

                {/* Main Analytics Sections */}
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-1">
                    <UsersAnalytics
                        transactionsData={transactionsAnalytics?.transactions}
                        dateRange={dateRange}
                    />
                    {isUserJobsEnabled && (
                        <UserJobsAnalytics
                            data={quickStats?.userJobs}
                            dateRange={dateRange}
                        />
                    )}
                    <UsageAnalytics
                        data={transactionsAnalytics}
                        dateRange={dateRange}
                    />
                    {is1TemplateTenant && (
                        <CreditsAnalytics
                            data={transactionsAnalytics}
                            dateRange={dateRange}
                        />
                    )}
                </div>
            </div>
        </PageWrapper>
    );
};
